import { Box, Flex, Spacer, Text } from '@chakra-ui/react';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { Color } from 'presentation/theme/colorScheme';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  days: Moment[];
  parentScrollLeft: number;
  events: ScheduledEventItem[];
  onEventClick: (id: ID) => void;
}

const EventCalendarMonthItem: FC<Props> = ({
  days,
  parentScrollLeft,
  events,
  onEventClick,
}) => {
  const scheduledEvents = useMemo<Record<string, ID>>(() => {
    return events.reduce(
      (val, item) => ({
        ...val,
        [moment(item.date).startOf('D').unix()]: item.eventId,
      }),
      {}
    );
  }, [events]);

  const { i18n } = useTranslation();

  const ref = useRef<HTMLDivElement>(null);
  const monthTextRef = useRef<HTMLDivElement>(null);
  let left = 0;
  let isAnchoredToRight = false;
  if (ref.current && monthTextRef.current) {
    const monthTextWidth = monthTextRef.current.clientWidth;
    if (
      parentScrollLeft >= ref.current.offsetLeft &&
      parentScrollLeft <
        ref.current.offsetLeft + ref.current.clientWidth - monthTextWidth
    ) {
      left = parentScrollLeft - ref.current.offsetLeft;
    } else if (
      parentScrollLeft >
      ref.current.offsetLeft + ref.current.clientWidth - monthTextWidth
    ) {
      isAnchoredToRight = true;
    }
  }

  return (
    <Box mr="36px" ref={ref} position="relative">
      <Spacer h="30px" />
      <Text
        ref={monthTextRef}
        h="20px"
        top="0"
        left={!isAnchoredToRight ? `${left}px` : undefined}
        right={isAnchoredToRight ? 0 : undefined}
        position="absolute"
      >
        {_.capitalize(days[0].locale(i18n.language).format('MMMM'))}
      </Text>
      <Flex minW="72px">
        {days.map((day, dayIndex) => {
          return (
            <Box
              py="5px"
              key={dayIndex}
              w="36px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDir="column"
              cursor="pointer"
              borderRadius="6px"
              _hover={{
                bgColor: Color.Shade200,
              }}
              onClick={() => {
                const eventId = scheduledEvents[day.unix().toString()];
                if (eventId) {
                  onEventClick(eventId);
                }
              }}
            >
              <Text fontSize="22px" fontWeight={700} mb="5px">
                {day.locale(i18n.language).format('D')}
              </Text>
              <Text
                color={
                  day.weekday() === 5 || day.weekday() === 6
                    ? Color.Danger500
                    : '#000'
                }
                mb="7px"
              >
                {day.locale(i18n.language).format('dd')}
              </Text>
              <Box
                w="10px"
                h="10px"
                borderRadius="50%"
                bgColor={
                  scheduledEvents[day.unix().toString()]
                    ? Color.Primary600
                    : undefined
                }
              />
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};

export default EventCalendarMonthItem;
